import { useState } from "react";
import { ChevronDown, ChevronRight, NotebookPen } from "lucide-react";
import { useAronNotebook } from "../api/aronApi";

interface Props {
  conversationId: string | null;
  /** Tura w toku — wtedy notatnik dopisuje się na żywo i trzeba go dopytywać. */
  busy?: boolean;
}

/** Notatnik zlecenia — ustalenia, na których ARON opiera decyzje w TEJ rozmowie
 * (id przedsięwzięcia, wybrany podwykonawca, id umowy ramowej, id zadania w tle).
 *
 * Po co to widać: gdy odpowiedź jest nie o tym, pierwsze pytanie brzmi „na czym on
 * to oparł?". Dotąd trzeba było pobierać zapis rozmowy i czytać sto wywołań API;
 * teraz ustalenia są wypisane w jednej linijce nad polem wiadomości, więc pomyłkę
 * (zła umowa, obce przedsięwzięcie) widać ZANIM ARON coś na jej podstawie zapisze.
 *
 * Tylko do wglądu: notatnik poprawia się słowem do asystenta, nie klikaniem —
 * inaczej człowiek i model mieliby dwie różne wersje tej samej pamięci. */
export function AronNotebook({ conversationId, busy = false }: Props) {
  const [open, setOpen] = useState(false);
  const { data: entries = [] } = useAronNotebook(conversationId, {
    pollMs: busy ? 5000 : 0,
  });

  if (!conversationId || entries.length === 0) return null;

  const scope = entries.find((e) => e.key === "zakres");
  const rest = entries.filter((e) => e.key !== "zakres");

  return (
    <div className="shrink-0 border-t border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 px-3 py-1 text-left text-[11px] text-gray-500 transition-colors hover:text-red-600 dark:text-gray-400"
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <NotebookPen size={12} className="shrink-0" />
        <span className="font-mono uppercase tracking-[0.14em]">
          Notatnik zlecenia
        </span>
        <span className="text-gray-400">({entries.length})</span>
        {!open && scope && (
          <span className="truncate text-gray-500 dark:text-gray-400">
            — {scope.value}
          </span>
        )}
      </button>
      {open && (
        <dl className="max-h-40 overflow-y-auto px-3 pb-2 text-xs">
          {[...(scope ? [scope] : []), ...rest].map((e) => (
            <div key={e.key} className="flex gap-2 py-0.5">
              <dt className="w-40 shrink-0 truncate font-mono text-[11px] text-gray-500 dark:text-gray-400">
                {e.key}
              </dt>
              <dd className="min-w-0 flex-1 break-words text-gray-700 dark:text-gray-200">
                {e.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
